import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { DashboardPage } from './pages/dashboard';
import { MembersPage } from './pages/members/members-page';
import { SandboxPage } from './pages/sandbox';
import { QuickStartPage } from './pages/sandbox/quick-start';
import { SandboxSessionDetailPage } from './pages/sandbox/sandbox-session-detail-page';
import { ChannelLogsPage } from './pages/channel-logs';
import { CliCallbackPage } from './pages/cli-callback';
import { CliCallbackInstagramPage } from './pages/cli-callback-instagram';
import { CliBillingCallbackPage } from './pages/cli-billing-callback';
import { MetaCallbackPage } from './pages/meta-callback';
import { MetaCallbackInstagramPage } from './pages/meta-callback-instagram';
import { FacebookLoginCallbackPage } from './pages/facebook-login-callback';
import { MetaConnectingPage } from './pages/meta-connecting';
import { SignupPage } from './pages/signup';
import { SandboxHandoffEntryPage } from './pages/sandbox-handoff-entry';
import { ConnectStartPage } from './pages/connect/connect-start';
import { InvitePage } from './pages/invite/invite-page';
import { InviteConnectPage } from './pages/invite/invite-connect-page';
import { InviteDonePage } from './pages/invite/invite-done-page';
import { ConnectCallbackPage } from './pages/connect/connect-callback';
import { ConnectDonePage, ConnectErrorPage } from './pages/connect/connect-done';
import { CardLinkPage } from './pages/card/card-link-page';
import { ProtectedRoute } from './components/protected-route';
import { SuperAdminRoute } from './components/super-admin-route';
import { SuperAdminLayout } from './pages/super-admin/super-admin-layout';
import { SuperAdminWorkspacesPage } from './pages/super-admin/super-admin-workspaces-page';
import { SuperAdminChannelsPage } from './pages/super-admin/super-admin-channels-page';
import { SuperAdminChannelDetailPage } from './pages/super-admin/super-admin-channel-detail-page';
import { SuperAdminWorkspaceDetailPage } from './pages/super-admin/super-admin-workspace-detail-page';
import { SuperAdminUsersPage } from './pages/super-admin/super-admin-users-page';
import { SuperAdminOrganizationsPage } from './pages/super-admin/super-admin-organizations-page';
import { SuperAdminOrganizationDetailPage } from './pages/super-admin/super-admin-organization-detail-page';
import { SuperAdminUserDetailPage } from './pages/super-admin/super-admin-user-detail-page';
import { SuperAdminMetaAppsPage } from './pages/admin/meta-apps/meta-apps-admin-page';
import { DashboardLayout } from './components/dashboard-layout';
import { WorkspaceLayout } from './components/workspace-layout';
import { OrgLayout } from './components/org-layout';
import { OrgWorkspacesPage } from './pages/org/org-workspaces-page';
import { OrgCustomersPage } from './pages/org/org-customers-page';
import { OrgDashboardPage } from './pages/org/org-dashboard-page';
import { OrgOrganizationPage } from './pages/org/org-organization-page';
import { OrgTeamMemberPage } from './pages/org/org-team-member-page';
import { OrgTeamPage } from './pages/org/org-team-page';
import { OrgBillingPage } from './pages/org/org-billing-page';
import { OrgPlansPage } from './pages/org/org-plans-page';
import { OnboardingPage } from './pages/org/onboarding-page';
import { OrgApiPage } from './pages/org/org-api-page';
import { LaunchpadPage } from './pages/launchpad';
import { OrgChannelsPage } from './pages/org/channels-page';
import { RootRedirect } from './components/root-redirect';
import { WorkspaceProvider } from './contexts/workspace-context';
import { ProfileProvider } from './contexts/profile-context';
import { Toaster } from '@/components/ui/sonner';
import { ErrorProvider } from '@/contexts/error-context';
import { AppErrorBoundary } from '@/components/error-boundary';
import { SentrySessionBinding } from '@/auth/session-hook';
import { useMetaOAuthCompletion } from '@/components/use-meta-signup-listeners';
import { useTrackOpenAIRegistration } from '@/hooks/use-track-openai-registration';
import { useSession } from '@/contexts/session-context';

/**
 * App-wide Meta OAuth completion mount. The connect popup can be launched
 * from any page (workspace chrome, org chrome, future surfaces), so the
 * completion listeners live once at the router root instead of per-layout —
 * a layout that forgets to wire them can no longer drop the OAuth code
 * (the 2026-07-15 org-page bug). Safe on public/transit pages: the popup's
 * postMessage targets its opener and same-tab localStorage writes don't fire
 * storage events, so only the launching tab ever reacts.
 */
function MetaOAuthCompletionMount() {
  useMetaOAuthCompletion();
  return null;
}

// AIT-639 — app-wide ChatGPT Ads registration mount. Router-rooted for the
// same reason as MetaOAuthCompletionMount (any post-auth route may be the
// signup's first page) and because it needs useLocation to wait out the
// credential-bearing /signup and /sandbox return URLs.
function OpenAIRegistrationMount() {
  const { user } = useSession();
  useTrackOpenAIRegistration(user);
  return null;
}

// Wrapped AFTER Sentry.init: main.tsx imports './instrument' (which runs
// initSentry()) before './app', so the SDK's router hooks are installed by
// the time this evaluates. Wrapping earlier (e.g. in lib/sentry.ts module
// scope) silently returns the unwrapped Routes and kills parameterized
// transaction names. With Sentry uninitialized (local dev), the HOC returns
// Routes unchanged — inert.
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

export function App() {
  return (
    <ErrorProvider>
      <AppErrorBoundary>
        <SentrySessionBinding />
        <BrowserRouter>
          <MetaOAuthCompletionMount />
          <OpenAIRegistrationMount />
          <SentryRoutes>
            {/* Public -- backend now owns the OAuth exchange and 302s the
                browser straight to returnTo, so the frontend /callback page
                is gone (Phase B sealed-session migration). The CLI variant
                stays -- its handoff state lives in the frontend. */}
            <Route path="/cli/callback" element={<CliCallbackPage />} />
            <Route path="/cli/callback/instagram" element={<CliCallbackInstagramPage />} />
            {/* AIT-397 — Stripe checkout return for a CLI-initiated upgrade.
                A CLI user paying in a browser has no dashboard session, so
                landing on /org/:orgId/billing (behind ProtectedRoute) would
                bounce them to AuthKit sign-in right after paying. This page
                is public and makes no API calls — the CLI itself polls the
                subscription and prints success. */}
            <Route path="/cli/billing/callback" element={<CliBillingCallbackPage />} />
            {/* 2026-05-23 refactor: all Meta OAuth flows land under
                /meta/callback/<provider>. One dedicated path per flow because
                Meta's redirect_uri matcher is strict on the scheme+host+path
                base (querystring discriminators get rejected). */}
            <Route path="/meta/callback/whatsapp" element={<MetaCallbackPage />} />
            <Route path="/meta/callback/instagram" element={<MetaCallbackInstagramPage />} />
            <Route path="/meta/callback/instagram-fb" element={<FacebookLoginCallbackPage />} />
            {/* Phase 129 Plan 09 (Codex review hardening) -- handoff JWT bridge.
                The marketing site's CTAs link to /signup?handoff=<jwt>; this
                route persists the JWT to sessionStorage and triggers WorkOS
                signUp(). Lives OUTSIDE ProtectedRoute because the user is
                unauthenticated at this point. */}
            <Route path="/signup" element={<SignupPage />} />
            {/* Phase 130 §4c -- top-level /sandbox handoff entry. Marketing CTA
                points here regardless of auth state: unauthed → signUp(); authed
                → redirect to /w/<active-ws>/sandbox so the protected SandboxPage
                handles the claim. */}
            <Route path="/sandbox" element={<SandboxHandoffEntryPage />} />
            {/* SaaS onboarding link entry point. /connect/:token calls
                GET /org/connect/:token/start and immediately navigates the
                customer's browser to the returned Meta authorize URL.
                /connect/callback handles the OAuth return, finalizes the
                connection, and redirects to the org-admin-configured success URL.
                Both routes are PUBLIC -- the end-customer has no HookMyApp account. */}
            {/* AIT-529 — affiliate invite consent screen. Public: the invited
                client has no account yet. */}
            <Route path="/invite/:slug" element={<InvitePage />} />
            {/* AIT-529 -- legacy landing from the WorkOS round-trip. signIn()
                now returns to /invite/connect directly (the grant runs in the
                background there), but a session already in flight can still
                come back here, so it renders the same picker. */}
            <Route
              path="/invite/complete"
              element={
                <ProtectedRoute>
                  <WorkspaceProvider>
                    <InviteConnectPage />
                  </WorkspaceProvider>
                </ProtectedRoute>
              }
            />
            {/* AIT-529 — the invited client's post-signup flow, deliberately
                OUTSIDE the workspace shell. They pick a channel
                (/invite/connect), the Meta code is exchanged on the standalone
                twin of the connecting page (/invite/connecting/:ws), and they
                finish on /invite/done. They never enter a workspace. */}
            <Route
              path="/invite/connect"
              element={
                <ProtectedRoute>
                  <WorkspaceProvider>
                    <InviteConnectPage />
                  </WorkspaceProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invite/connecting/:ws"
              element={
                <ProtectedRoute>
                  <WorkspaceProvider>
                    <MetaConnectingPage />
                  </WorkspaceProvider>
                </ProtectedRoute>
              }
            />
            {/* Terminal screen — PUBLIC so it renders nothing but the
                confirmation (a protected route would fire the whole authed
                workspace bootstrap behind it). */}
            <Route path="/invite/done" element={<InviteDonePage />} />
            <Route path="/connect/done" element={<ConnectDonePage />} />
            <Route path="/connect/error" element={<ConnectErrorPage />} />
            <Route path="/connect/:token" element={<ConnectStartPage />} />
            <Route path="/connect/callback" element={<ConnectCallbackPage />} />
            {/* AIT-505 Task 8 — public add-a-card link. The token travels in
                the URL FRAGMENT (/card#<token>), never a query param, so it's
                never sent to a server; CardLinkPage reads it from
                window.location.hash and immediately scrubs it from the
                visible URL. PUBLIC -- no WorkOS session. */}
            <Route path="/card" element={<CardLinkPage />} />

            {/* Root -- LandingPage (unauth) OR cascade to /w/:ws (auth) */}
            <Route
              path="/"
              element={
                <WorkspaceProvider>
                  <RootRedirect />
                </WorkspaceProvider>
              }
            />

            {/* Workspace-scoped -- DashboardLayout (chrome) wraps WorkspaceLayout
                (validation + switchWorkspace), both inside ProtectedRoute +
                WorkspaceProvider. Nested routes render through
                WorkspaceLayout's <Outlet /> which forwards the parent outlet
                context (onConnectClick). */}
            <Route
              path="/w/:ws"
              element={
                <ProtectedRoute>
                  <WorkspaceProvider>
                    <ProfileProvider>
                      <DashboardLayout />
                    </ProfileProvider>
                  </WorkspaceProvider>
                </ProtectedRoute>
              }
            >
              <Route element={<WorkspaceLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="channels" element={<DashboardPage />} />
                <Route path="channels/:channelId" element={<DashboardPage />} />
                <Route path="channels/:channelId/logs" element={<ChannelLogsPage />} />
                <Route path="channels/connecting" element={<MetaConnectingPage />} />
                <Route path="members" element={<MembersPage />} />
                <Route path="sandbox" element={<SandboxPage />} />
                <Route path="sandbox/quick-start" element={<QuickStartPage />} />
                <Route
                  path="sandbox/sessions/:sessionId"
                  element={<SandboxSessionDetailPage />}
                />
                <Route path="launchpad" element={<LaunchpadPage />} />
              </Route>
            </Route>

            {/* Organization view (R3, spec Decision 2) -- same provider stack
                as /w/:ws. OrgLayout validates :orgId itself via
                GET /organizations/:orgId (org admins may have zero workspace
                memberships); WorkspaceProvider is only a convenience for the
                org pages (refreshWorkspaces, usage in BillingSection);
                ProfileProvider feeds the shared AppHeader. */}
            <Route
              path="/org/:orgId"
              element={
                <ProtectedRoute>
                  <WorkspaceProvider>
                    <ProfileProvider>
                      <OrgLayout />
                    </ProfileProvider>
                  </WorkspaceProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<OrgDashboardPage />} />
              <Route path="workspaces" element={<OrgWorkspacesPage />} />
              <Route path="workspaces/channels" element={<OrgChannelsPage kind="team" />} />
              <Route path="customers" element={<OrgCustomersPage />} />
              <Route path="organization" element={<OrgOrganizationPage />} />
              <Route path="api" element={<OrgApiPage />} />
              <Route path="team" element={<OrgTeamPage />} />
              <Route path="team/:memberId" element={<OrgTeamMemberPage />} />
              <Route path="plans" element={<OrgPlansPage />} />
              <Route path="billing" element={<OrgBillingPage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="channels" element={<OrgChannelsPage kind="customer" />} />
            </Route>

            {/* User-scoped stub (Phase 114 fills /account/profile) */}
            <Route path="/account/*" element={<Navigate to="/" replace />} />

            {/* Operator -- unchanged */}
            <Route
              path="/super-admin"
              element={
                <SuperAdminRoute>
                  <SuperAdminLayout />
                </SuperAdminRoute>
              }
            >
              <Route index element={<SuperAdminWorkspacesPage />} />
              <Route path="organizations" element={<SuperAdminOrganizationsPage />} />
              <Route path="organizations/:id" element={<SuperAdminOrganizationDetailPage />} />
              <Route path="channels" element={<SuperAdminChannelsPage />} />
              <Route path="channels/:id" element={<SuperAdminChannelDetailPage />} />
              <Route path="workspaces/:id" element={<SuperAdminWorkspaceDetailPage />} />
              <Route path="users" element={<SuperAdminUsersPage />} />
              <Route path="users/:id" element={<SuperAdminUserDetailPage />} />
              <Route path="meta-apps" element={<SuperAdminMetaAppsPage />} />
            </Route>

            {/* Catch-all: handles defunct /dashboard/* bookmarks */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </SentryRoutes>
        </BrowserRouter>
        <Toaster />
      </AppErrorBoundary>
    </ErrorProvider>
  );
}
